import React from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import {
  Container,
  Card,
  Row,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import { useBoolean } from 'usehooks-ts';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import DataTableLocations from 'components/App/DataTableLocations';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import {
  placeTypeOptions,
} from 'components/AddLocation/StepBasic/config';
import ModalAddLocation from 'components/Modals/ModalAddLocation';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import ModalSuccess from 'components/Modals/ModalSuccess';
import Pagination from 'components/Pagination';

import images from 'assets/images';
import { LOCATIONS, MODAL_ADD_LOCATION_TAB, STATUS_LIST, themeColor } from 'constants/index';
import MODAL_TYPE from 'constants/modalType';
import cn from 'utils/classnames';
import getTotalPage from 'utils/totalPage';

import DownloadIcon from 'assets/images/download.svg';
import AddIcon from 'assets/images/add-white.svg';

import styles from './LocationList.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý chỗ nghỉ',
    isActive: false,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
  // {
  //   name: 'Danh sách chỗ nghỉ',
  //   isActive: true,
  //   link: '',
  // },
];

const TAB = {
  ALL: 'ALL',
  PENDING: 'PENDING',
  DISCOUNT: 'DISCOUNT',
};

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: TAB.ALL,
  },
  // Temporarily hidden
  // {
  //   title: 'Chờ duyệt',
  //   value: TAB.PENDING,
  // },
  // {
  //   title: 'Giảm giá',
  //   value: TAB.DISCOUNT,
  // },
];

const DEFAULT_LIMIT = 10;
const MARKET_PAGE_LIMIT = 8;
const INTERNAL_ACCOUNT_PAGE_LIMIT = 8;

const STAR_RATING_OPTIONS = [
  { label: '5 sao', value: 5 },
  { label: '4 sao', value: 4 },
  { label: '3 sao', value: 3 },
  { label: '2 sao', value: 2 },
  { label: '1 sao', value: 1 },
];

const STATUS_OPEN_CLOSE_OPTIONS = [
  { label: 'Mở', value: 'true' },
  { label: 'Đóng', value: 'false' },
];

const DEFAULT_SORT_BY = 'starRating';
const DEFAULT_SORT_ORDER = 'desc';

function LocationList(props) {
  const {
    isLoadingLocationList,
    isLoadingExportLocationList,

    modalType,
    locationList,
    totalLocation,
    marketList,
    marketTotal,
    isLoadingMarketList,
    landmarkOptions,
    isLoadingLandmarkList,
    internalAccountList,
    internalAccountTotal,
    isLoadingGetInternalAccountList,

    actionGetLocationList,
    actionExportLocationList,
    actionGetMarketList,
    actionGetLandmarkList,
    actionClearLandmarkList,
    actionGetInternalAccountList,
    actionDeleteLocation,
    actionCloseModalCreatePlaceSuccess,
  } = props;

  const {
    value: isOpenModalDeleteLocation,
    setTrue: onOpenModalDeleteLocation,
    setFalse: onCloseModalDeleteLocation,
    toggle: onToggleModalDeleteLocation,
  } = useBoolean(false);

  const {
    value: isActiveModalAddLocation,
    setTrue: onOpenModalAddLocation,
    setFalse: onCloseModalAddLocation,
  } = useBoolean(false);

  const history = useHistory();
  const [activeTab, setActiveTab] = React.useState(TAB.ALL);
  const [tabModalActive, setTabModalActive] = React.useState(
    MODAL_ADD_LOCATION_TAB.NEW,
  );

  const [locationIdDelete, setLocationIdDelete] = React.useState('');
  const [marketPage, setMarketPage] = React.useState(1);
  const [marketSearchQuery, setMarketSearchQuery] = React.useState('');
  const [selectedMarketLabel, setSelectedMarketLabel] = React.useState('');
  const [internalAccountPage, setInternalAccountPage] = React.useState(1);
  const [internalAccountSearchQuery, setInternalAccountSearchQuery] =
    React.useState('');
  const [selectedInternalAccountLabel, setSelectedInternalAccountLabel] =
    React.useState('');
  const [sortBy, setSortBy] = React.useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = React.useState(DEFAULT_SORT_ORDER);

  const hasMoreMarkets = marketList.length < marketTotal;
  const hasMoreInternalAccounts = internalAccountList.length < internalAccountTotal;

  const marketOptions = React.useMemo(
    () =>
      marketList.map((item) => ({
        label: item.name,
        value: item._id,
      })),
    [marketList],
  );

  const internalAccountOptions = React.useMemo(
    () =>
      internalAccountList.map((item) => ({
        label: item.fullName,
        value: item._id,
      })),
    [internalAccountList],
  );

  const urlPage = React.useMemo(
    () => Number(new URLSearchParams(history.location.search).get('page') || 1),
    [history.location.search],
  );

  const validation = useFormik({
    initialValues: {
      q: '',
      market: '',
      placeType: '',
      landmark: '',
      starRating: '',
      status: '',
      isPublished: '',
      partnerServiceManager: '',
    },
    onSubmit: () => {},
  });

  const filterValuesRef = React.useRef(validation.values);
  filterValuesRef.current = validation.values;

  const buildLocationListPayload = React.useCallback(
    (overrides = {}) => {
      const payload = {
        sortBy,
        sortOrder,
        page: urlPage,
        limit: DEFAULT_LIMIT,
      };

      Object.entries(filterValuesRef.current).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          payload[key] = value;
        }
      });

      return {
        ...payload,
        ...overrides,
      };
    },
    [sortBy, sortOrder, urlPage],
  );

  const fetchLocationList = React.useCallback(
    (overrides = {}) => {
      actionGetLocationList(buildLocationListPayload(overrides));
    },
    [actionGetLocationList, buildLocationListPayload],
  );

  const buildLocationExportPayload = React.useCallback(() => {
    const { page, limit, ...exportPayload } = buildLocationListPayload();

    return exportPayload;
  }, [buildLocationListPayload]);

  const onExportLocationList = React.useCallback(() => {
    actionExportLocationList(buildLocationExportPayload());
  }, [actionExportLocationList, buildLocationExportPayload]);

  const onSearchSubmit = React.useCallback(
    (event) => {
      event.preventDefault();
      fetchLocationList({ page: 1 });

      if (urlPage !== 1) {
        history.push('?page=1');
      }
    },
    [fetchLocationList, history, urlPage],
  );

  const onSort = React.useCallback(
    (columnSortBy) => {
      const nextSortOrder =
        sortBy === columnSortBy && sortOrder === 'desc' ? 'asc' : 'desc';
      const resolvedSortOrder =
        sortBy === columnSortBy ? nextSortOrder : DEFAULT_SORT_ORDER;

      setSortBy(columnSortBy);
      setSortOrder(resolvedSortOrder);

      if (urlPage !== 1) {
        history.push('?page=1');
        return;
      }

      fetchLocationList({
        sortBy: columnSortBy,
        sortOrder: resolvedSortOrder,
        page: 1,
      });
    },
    [fetchLocationList, history, sortBy, sortOrder, urlPage],
  );

  const onChangeFilterOption = React.useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const marketSelectValue = React.useMemo(() => {
    const marketId = validation.values.market;

    if (!marketId) {
      return null;
    }

    const labelFromList = marketList.find(
      (item) => item._id === marketId,
    )?.name;

    return {
      label: selectedMarketLabel || labelFromList || '',
      value: marketId,
    };
  }, [validation.values.market, selectedMarketLabel, marketList]);

  const onMarketChange = React.useCallback(
    (e) => {
      validation.setFieldValue('market', e?.value || '', true);
      validation.setFieldValue('landmark', '', true);
      setSelectedMarketLabel(e?.label || '');

      if (!e?.value) {
        actionClearLandmarkList();
        return;
      }

      if (e?.value) {
        actionGetLandmarkList({
          market: e.value,
          marketName: e.label,
        });
        return;
      }

      actionClearLandmarkList();
    },
    [
      actionClearLandmarkList,
      actionGetLandmarkList,
      validation,
    ],
  );

  const internalAccountSelectValue = React.useMemo(() => {
    const partnerServiceManagerId = validation.values.partnerServiceManager;

    if (!partnerServiceManagerId) {
      return null;
    }

    const labelFromList = internalAccountList.find(
      (item) => item._id === partnerServiceManagerId,
    )?.fullName;

    return {
      label: selectedInternalAccountLabel || labelFromList || '',
      value: partnerServiceManagerId,
    };
  }, [
    validation.values.partnerServiceManager,
    selectedInternalAccountLabel,
    internalAccountList,
  ]);

  const onInternalAccountChange = React.useCallback(
    (e) => {
      validation.setFieldValue('partnerServiceManager', e?.value || '', true);
      setSelectedInternalAccountLabel(e?.label || '');
    },
    [validation],
  );

  const buildMarketListPayload = React.useCallback(({ page, append, q }) => {
    const payload = {
      page,
      limit: MARKET_PAGE_LIMIT,
      append,
    };

    if (q) {
      payload.q = q;
    }

    return payload;
  }, []);

  const buildInternalAccountListPayload = React.useCallback(
    ({ page, append, q }) => {
      const payload = {
        page,
        limit: INTERNAL_ACCOUNT_PAGE_LIMIT,
        append,
      };

      if (q) {
        payload.q = q;
      }

      return payload;
    },
    [],
  );

  const debouncedSearchMarkets = React.useMemo(
    () =>
      debounce((searchValue) => {
        setMarketSearchQuery(searchValue);
        setMarketPage(1);
        actionGetMarketList(
          buildMarketListPayload({
            page: 1,
            append: false,
            q: searchValue,
          }),
        );
      }, 400),
    [actionGetMarketList, buildMarketListPayload],
  );

  const debouncedSearchInternalAccounts = React.useMemo(
    () =>
      debounce((searchValue) => {
        setInternalAccountSearchQuery(searchValue);
        setInternalAccountPage(1);
        actionGetInternalAccountList(
          buildInternalAccountListPayload({
            page: 1,
            append: false,
            q: searchValue,
          }),
        );
      }, 400),
    [actionGetInternalAccountList, buildInternalAccountListPayload],
  );

  React.useEffect(
    () => () => debouncedSearchMarkets.cancel(),
    [debouncedSearchMarkets],
  );

  React.useEffect(
    () => () => debouncedSearchInternalAccounts.cancel(),
    [debouncedSearchInternalAccounts],
  );

  const onMarketInputChange = React.useCallback(
    (inputValue, { action }) => {
      if (action === 'input-change') {
        debouncedSearchMarkets(inputValue);
      }
    },
    [debouncedSearchMarkets],
  );

  const resetMarketList = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    actionGetMarketList(
      buildMarketListPayload({ page: 1, append: false, q: '' }),
    );
  }, [actionGetMarketList, buildMarketListPayload, debouncedSearchMarkets]);

  const onMarketMenuOpen = React.useCallback(() => {
    resetMarketList();
  }, [resetMarketList]);

  const onMarketBlur = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
  }, [debouncedSearchMarkets]);

  const onLoadMoreMarkets = React.useCallback(() => {
    if (isLoadingMarketList || !hasMoreMarkets) {
      return;
    }

    const nextPage = marketPage + 1;
    setMarketPage(nextPage);
    actionGetMarketList(
      buildMarketListPayload({
        page: nextPage,
        append: true,
        q: marketSearchQuery,
      }),
    );
  }, [
    actionGetMarketList,
    buildMarketListPayload,
    hasMoreMarkets,
    isLoadingMarketList,
    marketPage,
    marketSearchQuery,
  ]);

  const onInternalAccountInputChange = React.useCallback(
    (inputValue, { action }) => {
      if (action === 'input-change') {
        debouncedSearchInternalAccounts(inputValue);
      }
    },
    [debouncedSearchInternalAccounts],
  );

  const resetInternalAccountList = React.useCallback(() => {
    debouncedSearchInternalAccounts.cancel();
    setInternalAccountSearchQuery('');
    setInternalAccountPage(1);
    actionGetInternalAccountList(
      buildInternalAccountListPayload({ page: 1, append: false, q: '' }),
    );
  }, [
    actionGetInternalAccountList,
    buildInternalAccountListPayload,
    debouncedSearchInternalAccounts,
  ]);

  const onInternalAccountMenuOpen = React.useCallback(() => {
    resetInternalAccountList();
  }, [resetInternalAccountList]);

  const onInternalAccountBlur = React.useCallback(() => {
    debouncedSearchInternalAccounts.cancel();
  }, [debouncedSearchInternalAccounts]);

  const onLoadMoreInternalAccounts = React.useCallback(() => {
    if (isLoadingGetInternalAccountList || !hasMoreInternalAccounts) {
      return;
    }

    const nextPage = internalAccountPage + 1;
    setInternalAccountPage(nextPage);
    actionGetInternalAccountList(
      buildInternalAccountListPayload({
        page: nextPage,
        append: true,
        q: internalAccountSearchQuery,
      }),
    );
  }, [
    actionGetInternalAccountList,
    buildInternalAccountListPayload,
    internalAccountPage,
    internalAccountSearchQuery,
    hasMoreInternalAccounts,
    isLoadingGetInternalAccountList,
  ]);

  const onResetForm = React.useCallback(() => {
    validation.resetForm();
    setSelectedMarketLabel('');
    setSelectedInternalAccountLabel('');
    debouncedSearchMarkets.cancel();
    debouncedSearchInternalAccounts.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    setInternalAccountSearchQuery('');
    setInternalAccountPage(1);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    actionClearLandmarkList();
    fetchLocationList({
      sortBy: DEFAULT_SORT_BY,
      sortOrder: DEFAULT_SORT_ORDER,
      page: 1,
      q: '',
      market: '',
      placeType: '',
      landmark: '',
      starRating: '',
      status: '',
      isPublished: '',
      partnerServiceManager: '',
    });

    if (urlPage !== 1) {
      history.push('?page=1');
    }
  }, [
    debouncedSearchInternalAccounts,
    debouncedSearchMarkets,
    actionClearLandmarkList,
    fetchLocationList,
    history,
    urlPage,
    validation,
  ]);

  const isSearchBtnDisabled = React.useMemo(
    () =>
      validation.values.q === '' &&
      validation.values.market === '' &&
      validation.values.placeType === '' &&
      validation.values.landmark === '' &&
      validation.values.starRating === '' &&
      validation.values.status === '' &&
      validation.values.isPublished === '' &&
      validation.values.partnerServiceManager === '',
    [validation],
  );

  const onChangeTab = React.useCallback(
    (tab) => () => {
      setActiveTab(tab);
    },
    [],
  );

  const onChangePage = React.useCallback(
    (selected) => {
      history.push(`?page=${selected.selected + 1}`);
      // tableRef.current.scrollIntoView({ behavior: 'smooth' });
    },
    [history],
  );

  const onToggleAddLocation = React.useCallback(() => {
    if (isActiveModalAddLocation) {
      onCloseModalAddLocation();
    } else {
      onOpenModalAddLocation();
    }

    if (isActiveModalAddLocation) {
      setTabModalActive(MODAL_ADD_LOCATION_TAB.NEW);
    }
  }, [
    isActiveModalAddLocation,
    onOpenModalAddLocation,
    onCloseModalAddLocation,
  ]);

  const onClickTabModalActive = React.useCallback(
    (tab) => () => {
      setTabModalActive(tab);
    },
    [],
  );

  const onSubmitModal = React.useCallback(
    (tab) => () => {
      switch (tab) {
        case MODAL_ADD_LOCATION_TAB.ALREADY:
          history.push(LOCATIONS.ADD_ALREADY_LOCATION);
          break;

        case MODAL_ADD_LOCATION_TAB.NEW:
          history.push(LOCATIONS.LOCATION_ADD);
          break;

        default:
          break;
      }
    },
    [history],
  );

  const pageCount = React.useMemo(
    () => getTotalPage(totalLocation, DEFAULT_LIMIT),
    [totalLocation],
  );

  const onDeleteLocation = React.useCallback(
    (locationId) => {
      setLocationIdDelete(locationId);
      onOpenModalDeleteLocation();
    },
    [onOpenModalDeleteLocation],
  );

  const onConfirmDeleteLocation = React.useCallback(() => {
    actionDeleteLocation({
      locationId: locationIdDelete,
      callback: onCloseModalDeleteLocation,
    });
  }, [actionDeleteLocation, locationIdDelete, onCloseModalDeleteLocation]);

  const onCloseModalSuccess = React.useCallback(() => {
    actionCloseModalCreatePlaceSuccess();
  }, [actionCloseModalCreatePlaceSuccess]);

  React.useEffect(() => {
    fetchLocationList({ page: urlPage });
  }, [urlPage, fetchLocationList]);

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name="Danh sách chỗ nghỉ" />

        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumb
            pageTitle="Quản lý chỗ nghỉ"
            breadcrumbList={breadcrumbList}
          />

          <Card className="Section">
            <form onSubmit={onSearchSubmit}>
              <Row className="Section__Search__Filter">
                <div className="mb-24 flex-1">
                  <Label className="Section__Search__Label">Tìm kiếm</Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm theo ID, Tên, Email và Số điện thoại của khách hàng"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input')}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </div>

                <div className="flex-2">
                  <Label className="Section__Search__Label">Bộ lọc</Label>

                  <div className="Section__Filter__Container">
                    <AppSelectValidation
                      placeholder="Thị trường"
                      name="market"
                      key="market"
                      options={marketOptions}
                      validation={validation}
                      value={marketSelectValue}
                      isUsePureValue
                      onChange={onMarketChange}
                      onInputChange={onMarketInputChange}
                      onBlur={onMarketBlur}
                      onMenuOpen={onMarketMenuOpen}
                      onMenuScrollToBottom={onLoadMoreMarkets}
                      filterOption={() => true}
                      isLoading={isLoadingMarketList}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Khu vực"
                      name="landmark"
                      key={`landmark-${validation.values.market}`}
                      options={landmarkOptions}
                      validation={validation}
                      value={validation.values.landmark}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                      isGroup
                      isDisabled={!validation.values.market}
                      isLoading={isLoadingLandmarkList}
                    />

                    <AppSelectValidation
                      placeholder="Loại hình"
                      name="placeType"
                      key="placeType"
                      options={placeTypeOptions}
                      validation={validation}
                      value={validation.values.placeType}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Hạng sao"
                      name="starRating"
                      key="starRating"
                      options={STAR_RATING_OPTIONS}
                      validation={validation}
                      value={validation.values.starRating}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Trạng thái"
                      name="status"
                      id="filter-status"
                      key="status"
                      options={STATUS_LIST}
                      validation={validation}
                      value={validation.values.status}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Trạng thái đóng mở"
                      name="isPublished"
                      key="isPublished"
                      options={STATUS_OPEN_CLOSE_OPTIONS}
                      validation={validation}
                      value={validation.values.isPublished}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Người phụ trách"
                      name="partnerServiceManager"
                      key="partnerServiceManager"
                      options={internalAccountOptions}
                      validation={validation}
                      value={internalAccountSelectValue}
                      isUsePureValue
                      onChange={onInternalAccountChange}
                      onInputChange={onInternalAccountInputChange}
                      onBlur={onInternalAccountBlur}
                      onMenuOpen={onInternalAccountMenuOpen}
                      onMenuScrollToBottom={onLoadMoreInternalAccounts}
                      filterOption={() => true}
                      isLoading={isLoadingGetInternalAccountList}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />
                  </div>
                </div>
              </Row>

              <div className="d-flex mt-24">
                <Button
                  outline
                  classes="Section__Search__Button__ReType"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </form>
          </Card>

          <Card className="Section">
            <div className="d-flex Table__Header">
              <Nav pills className="col-md-10 d-none">
                {NAV_TAB.map((tab) => (
                  <NavItem key={tab.value} className="col-md-2">
                    <NavLink
                      className={activeTab === tab.value ? 'active' : ''}
                      onClick={onChangeTab(tab.value)}
                    >
                      <span className="d-none d-sm-block">{tab.title}</span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <Label className="Table__Header__Total">
                {`${totalLocation} đối tác`}
              </Label>

              <div className="d-flex gap-2">
                <Button
                  outline
                  classes="Table__Header__Button__ReType"
                  color={themeColor.LIGHT}
                  disabled={isLoadingExportLocationList}
                  onClick={onExportLocationList}
                >
                  <img src={DownloadIcon} alt="download" />
                  <span>Xuất file</span>
                </Button>
                <Button
                  classes="Table__Header__Button__Add"
                  onClick={onToggleAddLocation}
                >
                  <img src={AddIcon} alt="add" />
                  <span>Thêm chỗ nghỉ</span>
                </Button>
              </div>
            </div>

            <TabContent activeTab={activeTab} className="Table__Body">
              {NAV_TAB.map((item) => (
                <TabPane tabId={item.value} key={item.value}>
                  <DataTableLocations
                    isLoadingLocationList={isLoadingLocationList}
                    locations={locationList}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    onDeleteLocation={onDeleteLocation}
                  />
                </TabPane>
              ))}
            </TabContent>
          </Card>

          <Pagination
            page={urlPage}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        </Container>

        <ModalAddLocation
          isActiveModalAddLocation={isActiveModalAddLocation}
          tabModalActive={tabModalActive}
          onToggleAddLocation={onToggleAddLocation}
          onClickTabModalActive={onClickTabModalActive}
          onSubmitModal={onSubmitModal}
        />

        <ModalSuccess
          isOpen={modalType === MODAL_TYPE.CREATE_PLACE_SUCCESS}
          onCloseModal={onCloseModalSuccess}
          message="Bạn đã tạo chỗ nghỉ thành công"
        />
      </div>

      <ModalRequestAction
        isOpen={isOpenModalDeleteLocation}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xóa \n chỗ nghỉ này không?'}
        onToggle={onToggleModalDeleteLocation}
        onCloseModal={onCloseModalDeleteLocation}
        onConfirm={onConfirmDeleteLocation}
      />
    </>
  );
}

LocationList.propTypes = {
  isLoadingLocationList: PropTypes.bool.isRequired,
  isLoadingExportLocationList: PropTypes.bool,
  isLoadingMarketList: PropTypes.bool,
  isLoadingLandmarkList: PropTypes.bool,
  isLoadingGetInternalAccountList: PropTypes.bool,

  modalType: PropTypes.string,
  locationList: PropTypes.instanceOf(Array),
  totalLocation: PropTypes.number,
  marketList: PropTypes.instanceOf(Array),
  marketTotal: PropTypes.number,
  landmarkOptions: PropTypes.instanceOf(Array),
  internalAccountList: PropTypes.instanceOf(Array),
  internalAccountTotal: PropTypes.number,

  actionGetLocationList: PropTypes.func.isRequired,
  actionExportLocationList: PropTypes.func.isRequired,
  actionGetMarketList: PropTypes.func.isRequired,
  actionGetLandmarkList: PropTypes.func.isRequired,
  actionClearLandmarkList: PropTypes.func.isRequired,
  actionGetInternalAccountList: PropTypes.func.isRequired,
  actionDeleteLocation: PropTypes.func.isRequired,
  actionCloseModalCreatePlaceSuccess: PropTypes.func.isRequired,
};

LocationList.defaultProps = {
  modalType: '',
  locationList: [],
  totalLocation: 0,
  isLoadingExportLocationList: false,
  marketList: [],
  marketTotal: 0,
  isLoadingMarketList: false,
  landmarkOptions: [],
  isLoadingLandmarkList: false,
  internalAccountList: [],
  internalAccountTotal: 0,
  isLoadingGetInternalAccountList: false,
};

export default LocationList;
